import { element, translate } from "./identity-view-primitives.js";

function formattedTimestamp(value) {
  try {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(value));
  } catch (_error) {
    return value;
  }
}

const SETTINGS_TAB_GROUPS = Object.freeze([
  Object.freeze({
    id: "account",
    label: Object.freeze(["account.settings.group.account", "Account"]),
    panels: Object.freeze(["profile"]),
    className: "grid min-w-0 gap-1.5",
  }),
  Object.freeze({
    id: "sign-in",
    label: Object.freeze(["account.settings.group.sign_in", "Sign-in methods"]),
    panels: Object.freeze(["password", "oidc", "passkey"]),
    className: "grid min-w-0 gap-1.5 sm:col-span-2 lg:col-span-2",
  }),
  Object.freeze({
    id: "two-step",
    label: Object.freeze(["account.settings.group.two_step", "Two-step verification"]),
    panels: Object.freeze(["webauthn", "totp", "lookup_secret"]),
    className: "grid min-w-0 gap-1.5 sm:col-span-2 lg:col-span-2",
  }),
  Object.freeze({
    id: "sessions",
    label: Object.freeze(["account.settings.group.sessions", "Sessions"]),
    panels: Object.freeze(["sessions"]),
    className: "grid min-w-0 gap-1.5",
  }),
  Object.freeze({
    id: "danger",
    label: Object.freeze(["account.settings.group.danger", "Danger zone"]),
    panels: Object.freeze(["delete-account"]),
    className: "grid min-w-0 gap-1.5",
  }),
]);

function sessionList(projection) {
  const section = element("section", {
    className: "card card-border bg-base-100 shadow-sm",
  });
  const body = element("div", { className: "card-body gap-4" });
  section.append(body);
  body.append(element("h2", {
    className: "font-semibold text-base-content",
    text: translate("account.sessions.title", "Other signed-in sessions"),
  }));
  if (projection.kind !== "ready") {
    const unavailable = element("p", {
      className: "alert alert-warning text-sm",
      text: projection.kind === "unsupported"
        ? translate(
            "account.sessions.unsupported",
            "Your other sessions cannot be displayed safely in this version.",
          )
        : translate(
            "account.sessions.unavailable",
            "Your other sessions are unavailable right now. Profile and sign-out controls still work.",
          ),
    });
    unavailable.setAttribute("role", "status");
    body.append(unavailable);
    return section;
  }
  const otherSessions = projection.sessions;
  if (otherSessions.length === 0) {
    body.append(element("p", {
      className: "text-sm text-base-content/65",
      text: translate("account.sessions.none", "No other active sessions."),
    }));
    return section;
  }
  const list = element("ul", { className: "list rounded-box bg-base-200/45" });
  for (const [index, session] of otherSessions.entries()) {
    const item = element("li", {
      className: "list-row items-center",
    });
    const detail = element("div", { className: "list-col-grow grid gap-1" });
    detail.append(
      element("p", {
        className: "text-sm font-medium",
        text: translate("account.sessions.item", `Signed-in session ${index + 1}`),
      }),
      element("p", {
        className: "text-xs text-base-content/60",
        text: `${translate("account.sessions.signed_in", "Signed in")} ${formattedTimestamp(
          session.authenticatedAt,
        )} · ${translate("account.sessions.expires", "expires")} ${formattedTimestamp(
          session.expiresAt,
        )}`,
      }),
    );
    const revoke = element("button", {
      className: "btn btn-sm btn-outline w-fit",
      text: translate("account.sessions.revoke", "Sign out session"),
    });
    revoke.type = "button";
    revoke.dataset.accountSessionRevoke = session.id;
    item.append(detail, revoke);
    list.append(item);
  }
  const revokeAll = element("button", {
    className: "btn btn-sm btn-outline w-fit",
    text: translate("account.sessions.revoke_all", "Sign out all other devices"),
  });
  revokeAll.type = "button";
  revokeAll.dataset.accountSessionsRevokeAll = "";
  revokeAll.dataset.accountConfirm = translate(
    "account.confirm.revoke_sessions",
    "Sign out every other active session?",
  );
  body.append(list, revokeAll);
  return section;
}

export function createAccountDeletionView(confirmation) {
  const section = element("section", {
    className: "card card-border border-error/40 bg-base-100 shadow-sm",
  });
  section.dataset.accountDeletionSection = "";
  const body = element("div", { className: "card-body gap-6" });
  section.append(body);
  const header = element("div", { className: "grid gap-2" });
  header.append(element("h2", {
    className: "font-semibold text-error",
    text: translate("account.deletion.title", "Delete account"),
  }));
  header.append(element("p", {
    className: "text-sm text-base-content/80",
    text: translate(
      "account.deletion.warning",
      "Deleting your account cannot be undone. It removes your FishyStuff account and synchronized data, and signs you out on every device.",
    ),
  }));
  body.append(header);

  const form = element("form", { className: "grid max-w-xl gap-3" });
  form.dataset.accountDeleteForm = "";
  const label = element("fieldset", { className: "fieldset min-w-0 grid-cols-1 gap-2" });
  const prompt = element("legend", {
    className: "fieldset-legend",
    text: translate(
      "account.deletion.confirm_prompt",
      "Type this handle exactly to confirm:",
    ),
  });
  const handle = element("code", {
    className: "w-fit rounded bg-base-200 px-2 py-1 text-sm",
    text: confirmation,
  });
  const input = element("input", { className: "input input-sm w-full" });
  input.type = "text";
  input.autocomplete = "off";
  input.autocapitalize = "none";
  input.spellcheck = false;
  input.required = true;
  input.dataset.accountDeleteConfirmation = confirmation;
  label.append(prompt, handle, input);

  const action = element("button", {
    className: "btn btn-sm btn-error w-fit",
    text: translate("account.deletion.action", "Delete account"),
  });
  action.type = "submit";
  action.disabled = true;
  action.dataset.accountDeleteSubmit = "";
  const status = element("p", { className: "text-sm text-base-content/65" });
  status.dataset.accountDeleteStatus = "";
  status.setAttribute("role", "status");
  status.setAttribute("aria-live", "polite");
  form.append(label, action, status);
  body.append(
    form,
    element("p", {
      className: "text-xs text-base-content/65",
      text: translate(
        "account.deletion.retention",
        "Server-side account data and synchronized private history are deleted. Minimal-context fishing statistics merged during deletion remain without a fixed deletion date and may be shared or published as explained in the Privacy Policy. FishyStuff does not keep an account-to-contribution link, but outside information may still allow association. Data stored on your devices is not deleted. Backups, temporary deletion records, security logs, and legal records follow the retention periods in the Privacy Policy.",
      ),
    }),
  );
  return section;
}

export function updateAccountDeletionView(root, state, context = null) {
  const input = root?.querySelector?.("[data-account-delete-confirmation]");
  const submit = root?.querySelector?.("[data-account-delete-submit]");
  const status = root?.querySelector?.("[data-account-delete-status]");
  if (!input || !submit || !status) return;

  const exactConfirmation = input.dataset.accountDeleteConfirmation;
  const locked = ["pending", "reauthenticationRequired"].includes(state);
  input.disabled = locked;
  submit.disabled = locked || input.value !== exactConfirmation;
  status.replaceChildren();
  status.className = "text-sm text-base-content/65";
  status.setAttribute("role", "status");

  if (state === "pending") {
    status.textContent = translate(
      "account.deletion.pending",
      "Deleting your account…",
    );
    return;
  }
  if (state === "retryable") {
    status.className = "text-sm text-error";
    status.setAttribute("role", "alert");
    status.textContent = translate(
      "account.deletion.retryable",
      "Account deletion could not be completed. Try again.",
    );
    return;
  }
  if (state === "identityChanged") {
    status.className = "text-sm text-warning";
    status.setAttribute("role", "alert");
    status.textContent = translate(
      "account.deletion.identity_changed",
      "We checked your account again. Confirm the current handle before deleting it.",
    );
    return;
  }
  if (state === "reauthenticationRequired") {
    input.value = "";
    status.className = "grid gap-2 text-sm text-error";
    status.setAttribute("role", "alert");
    status.append(element("span", {
      text: translate(
        "account.deletion.reauthenticate",
        "For your security, sign in again before deleting your account.",
      ),
    }));
    const link = element("button", {
      className: "link link-error w-fit font-medium",
      text: translate("account.deletion.reauthenticate_action", "Sign in again"),
    });
    link.type = "button";
    link.dataset.accountReauthenticationStart = "";
    status.append(link);
  }
}

function createAccountDeletionResultView(pending) {
  const root = element("div", { className: "grid gap-3" });
  if (pending) root.dataset.accountDeletionPending = "";
  else root.dataset.accountDeleted = "";
  root.tabIndex = -1;
  root.setAttribute("role", "status");
  root.setAttribute("aria-live", "polite");
  root.append(
    element("h1", {
      className: "text-3xl font-bold tracking-tight text-base-content",
      text: pending
        ? translate("account.deletion.accepted_title", "Account deletion started")
        : translate("account.deletion.complete_title", "Account deleted"),
    }),
    element("p", {
      className: "text-sm text-base-content/80",
      text: pending
        ? translate(
          "account.deletion.accepted_detail",
          "Your account data is locked and cleanup is continuing. You will be signed out on every device when deletion finishes.",
        )
        : translate(
          "account.deletion.complete_detail",
          "Your FishyStuff account and synchronized data were deleted. You have been signed out on every device.",
        ),
    }),
    element("p", {
      className: "text-sm text-base-content/65",
      text: translate(
        "account.deletion.complete_local",
        "Browser-local and offline data on this or other devices was not erased.",
      ),
    }),
    element("p", {
      className: "text-xs text-base-content/60",
      text: translate(
        "account.deletion.retention",
        "Server-side account data and synchronized private history are deleted. Minimal-context fishing statistics merged during deletion remain without a fixed deletion date and may be shared or published as explained in the Privacy Policy. FishyStuff does not keep an account-to-contribution link, but outside information may still allow association. Data stored on your devices is not deleted. Backups, temporary deletion records, security logs, and legal records follow the retention periods in the Privacy Policy.",
      ),
    }),
  );
  return root;
}

export function createAccountDeletedView() {
  return createAccountDeletionResultView(false);
}

export function createAccountDeletionPendingView() {
  return createAccountDeletionResultView(true);
}

export function createAccountSettingsView(
  settingsView,
  logoutUrl,
  otherSessions = Object.freeze({ kind: "ready", sessions: Object.freeze([]) }),
  deletionView = null,
  settingsPanelUrls = Object.freeze({}),
) {
  const methods = settingsView.querySelector("[data-account-settings-methods]");
  const idPrefix = settingsView.dataset.accountSettingsIdPrefix;
  if (!methods || !idPrefix) {
    throw new TypeError("Account settings view is missing its method contract");
  }

  const tablist = element("nav", {
    className:
      "grid min-w-0 gap-3 rounded-box border border-base-300 bg-base-200/45 p-3 sm:grid-cols-2 lg:grid-cols-4",
  });
  tablist.setAttribute(
    "aria-label",
    translate("account.settings.title", "Account settings"),
  );
  tablist.dataset.accountSettingsTabs = "";
  const tabGroups = new Map();
  for (const group of SETTINGS_TAB_GROUPS) {
    const root = element("div", { className: group.className });
    root.dataset.accountSettingsTabGroup = group.id;
    const label = translate(group.label[0], group.label[1]);
    root.append(element("span", {
      className: group.id === "danger"
        ? "px-1 text-xs font-semibold text-error"
        : "px-1 text-xs font-semibold text-base-content/55",
      text: label,
    }));
    const tabs = element("div", {
      className: "tabs tabs-box tabs-md min-w-0 w-full overflow-x-auto bg-base-100",
    });
    tabs.setAttribute("aria-label", label);
    root.append(tabs);
    tabGroups.set(group.id, Object.freeze({ root, tabs }));
    tablist.append(root);
  }
  const panels = element("div", { className: "min-w-0" });
  panels.dataset.accountSettingsPanels = "";

  const appendPanel = (id, label, content, { danger = false, hasError = false } = {}) => {
    const tabId = `${idPrefix}-${id}-tab`;
    const panelId = `${idPrefix}-${id}-panel`;
    const group = SETTINGS_TAB_GROUPS.find(({ panels: ids }) => ids.includes(id));
    if (!group) throw new TypeError(`Account settings panel group is missing: ${id}`);
    const tab = element("a", {
      className: danger
        ? "tab min-w-fit flex-1 whitespace-nowrap px-3 text-error"
        : "tab min-w-fit flex-1 whitespace-nowrap px-3",
      text: label,
    });
    tab.href = settingsPanelUrls[id] ?? "";
    tab.id = tabId;
    tab.dataset.accountSettingsTab = id;
    tab.setAttribute("aria-controls", panelId);

    const panel = element("section", { className: "grid min-w-0 gap-6 pt-6" });
    panel.id = panelId;
    panel.dataset.accountSettingsPanel = id;
    if (hasError) panel.dataset.accountSettingsHasError = "true";
    panel.setAttribute("aria-labelledby", tabId);
    panel.hidden = true;
    panel.append(content);
    tabGroups.get(group.id).tabs.append(tab);
    panels.append(panel);
  };

  for (const form of [...methods.children]) {
    const method = form.dataset.accountSettingsMethod;
    const label = form.dataset.accountSettingsLabel;
    if (!method || !label) {
      throw new TypeError("Account settings method is missing its presentation contract");
    }
    appendPanel(method, label, form, {
      hasError: form.dataset.accountSettingsHasError === "true",
    });
  }
  methods.remove();

  const sessions = element("div", { className: "grid gap-5" });
  sessions.append(sessionList(otherSessions));
  const logout = element("a", {
    className: "btn btn-sm btn-outline w-fit",
    text: translate("account.action.sign_out", "Sign out"),
  });
  logout.href = logoutUrl;
  logout.rel = "nofollow";
  logout.dataset.accountSignOut = "";
  const mutationStatus = element("p", { className: "text-sm text-base-content/65" });
  mutationStatus.dataset.accountSessionMutationStatus = "";
  mutationStatus.setAttribute("role", "status");
  mutationStatus.setAttribute("aria-live", "polite");
  sessions.append(logout, mutationStatus);
  appendPanel(
    "sessions",
    translate("account.sessions.tab", "Sessions"),
    sessions,
  );
  if (deletionView) {
    appendPanel(
      "delete-account",
      translate("account.deletion.title", "Delete account"),
      deletionView,
      { danger: true },
    );
  }

  for (const { root, tabs } of tabGroups.values()) {
    if (tabs.childElementCount === 0) root.remove();
  }

  settingsView.append(tablist, panels);
  return settingsView;
}
